<template>
    <fieldset class="mx-2">
        <legend align="center" v-html="$t('orderCheckout.select-payment-method')"></legend>

        <form ref="paymentForm"
              method="post"
              :action="formAction"
              class="paymentmethods"
              :class="{all: showAll}"
        >
            <input type="hidden" name="_token" :value="csrfToken" />
            <input type="hidden" name="subtype" :value="subMethod" />

            <div v-if="loadingPaymentMethods && !order.auto_renew" name="placeholders" class="placeholder-glow">
                <div class="placeholder payment-method" key="1"></div>
                <div class="placeholder payment-method" key="2"></div>
                <div class="placeholder payment-method" key="3"></div>
                <div class="placeholder payment-method" key="4"></div>
            </div>

            <div v-show="!loadingPaymentMethods || order.auto_renew">
                <template v-if="!order.auto_renew">
                    <template v-for="paymentMethod in paymentMethods">
                        <PlatformPayment
                            :key="'platform_' + (paymentMethod.subtype_id ? paymentMethod.subtype_id : paymentMethod.id)"
                            v-if="paymentMethod.id === 'stripe' && paymentMethod.subtype_id === 'card'"
                            payment-mode="payment"
                            currency="eur"
                            :stripe-payment-intent-api-url="stripePaymentIntentApiUrl"
                            v-bind:loadingPaymentMethods="loadingPaymentMethods"
                            @loaded="loadingPaymentMethods = $event"
                        />
                    </template>
                </template>

                <template v-for="paymentMethod in filteredPaymentMethods">
                    <div
                        v-if="paymentMethod.id === 'gift_voucher'"
                        :key="paymentMethod.subtype_id ? paymentMethod.subtype_id : paymentMethod.id"
                        class="paymentmethod"
                        @click="onPaymentOptionClick($event, paymentMethod)"
                        @touch="onPaymentOptionClick($event, paymentMethod)"
                    >
                        <div :class="paymentMethod.id" class="payment-method payment-method-container">
                            <label class="font-weight-bold mt-2">
                                {{ $t(paymentMethod.name) }}
                                <span v-if="typeof paymentMethod.usageFee === 'number' && paymentMethod.usageFee !== 0">
                                {{ $t('shop.paymentFee', { fee: paymentMethod.usageFee.toFixed(2) }) }}
                            </span>
                            </label>
                            <i class="payment-icon payment-icon-image">
                                <svg height="1em" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                                    <!--! Font Awesome Free 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
                                    <path
                                        d="M190.5 68.8L225.3 128H224 152c-22.1 0-40-17.9-40-40s17.9-40 40-40h2.2c14.9 0 28.8 7.9 36.3 20.8zM64 88c0 14.4 3.5 28 9.6 40H32c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32H480c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H438.4c6.1-12 9.6-25.6 9.6-40c0-48.6-39.4-88-88-88h-2.2c-31.9 0-61.5 16.9-77.7 44.4L256 85.5l-24.1-41C215.7 16.9 186.1 0 154.2 0H152C103.4 0 64 39.4 64 88zm336 0c0 22.1-17.9 40-40 40H288h-1.3l34.8-59.2C329.1 55.9 342.9 48 357.8 48H360c22.1 0 40 17.9 40 40zM32 288V464c0 26.5 21.5 48 48 48H224V288H32zM288 512H432c26.5 0 48-21.5 48-48V288H288V512z"/>
                                </svg>
                            </i>
                        </div>
                    </div>

                    <div
                        v-else-if="(!order.auto_renew || paymentMethod.supportsSubscription) && disablePaymentMethod !== paymentMethod.id"
                        :key="paymentMethod.subtype_id ? paymentMethod.subtype_id : paymentMethod.id"
                        class="paymentmethod"
                        @click="onPaymentOptionClick($event, paymentMethod)"
                        @touch="onPaymentOptionClick($event, paymentMethod)"
                    >
                        <div :class="paymentMethod.class ? paymentMethod.class : (paymentMethod.subtype_id || paymentMethod.id)" class="payment-method">
                            <label class="font-weight-bold mt-2">
                                {{ $t(paymentMethod.name) }}
                                <span v-if="typeof paymentMethod.usageFee === 'number' && paymentMethod.usageFee !== 0">
                                    {{ $t('shop.paymentFee', { fee: paymentMethod.usageFee.toFixed(2) }) }}
                                </span>
                            </label>
                            <i
                                v-if="!paymentMethod.iconPath"
                                :class="paymentMethod.icon"
                                class="payment-icon pf"
                            ></i>
                            <img v-else :alt="$t(paymentMethod.name)" class="payment-icon" :src="paymentMethod.iconPath" />
                        </div>
                    </div>
                </template>
            </div>

            <template v-if="order.auto_renew">
                <div class="no-mobile-padding mt-2">
                    <button
                        class="btn btn-outline-primary w-100"
                        @click="
                        $event.preventDefault();
                        $emit('terminate-subscription');
                    "
                    >
                        {{ $t('shop.subscriptionMorePaymentMethods') }}
                    </button>
                </div>
            </template>

            <a class="more ml-1 mt-2" href="#" @click.prevent="showMore" v-if="paymentMethods.length > 3 && !loadingPaymentMethods">
                {{ $t('orderCheckout.more-payment-methods') }} <i class="mdi mdi-chevron-right" :class="{active: showAll}"></i>
            </a>

            <GiftVoucherPaymentModal
                v-if="giftVoucherMethodAvailable"
                :order-hash="order.hash"
                :order-price="order.unpaid_amount"
                ref="giftVoucherPaymentModal" />

            <PaymentNoticeModal
                v-if="hasEnabledDelayNotice"
                v-bind="paymentNoticeModal"
                @continue="$refs.paymentForm.submit()"
                @close="paymentNoticeModal = {show: false, close: true, closeable: true}" />

            <BillingDetailsModal
                :countries="countries"
                :previous-first-name="this.billingData.first_name"
                :previous-last-name="this.billingData.last_name"
                :order="this.order"
                ref="billingDetailsModal"
                @billing-data-filled="onBillingDataFilled()"
            />
        </form>
    </fieldset>
</template>

<script>
import BillingDetailsModal from './Billing/BillingDetailsModal.vue';
import PaymentNoticeModal from './PaymentNoticeModal.vue';
import GiftVoucherPaymentModal from '../Payment/GiftVoucherPaymentModal.vue';
import PlatformPayment from '../Payment/PlatformPayment.vue';

export default {
    name: 'OrderCheckoutPaymentMethods',
    components: {BillingDetailsModal, PlatformPayment, GiftVoucherPaymentModal, PaymentNoticeModal},
    inject: ['order'],
    props: {
        countries: {
            type: Array,
            required: true,
        },
        csrfToken: {
            type: String,
            required: true,
        },
        paymentMethods: {
            type: Array,
            required: true,
            default() {
                return [
                    {
                        id: 'example',
                        name: 'Example',
                        icon: null,
                        enabled: false,
                        subtypes: {
                            example: {
                                enabled: false,
                                sort: 0,
                                id: 'example',
                                delayNotice: {
                                    disabled: false,
                                    title: 'shop.delayNoticeTitle',
                                    text: 'shop.delayNoticeText',
                                },
                                name: 'shop.example',
                                icon: 'pf-bank-transfer',
                            },
                        },
                    },
                ];
            },
        },
        disablePaymentMethod: {
            type: String,
            required: false,
        },
        price: {
            type: Number,
            required: true,
        },
        stripePaymentIntentApiUrl: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            loading: false,
            loadingPaymentMethods: true,
            showAll: false,
            method: null,
            subMethod: null,
            askForBillingData: false,
            askForBillingDataDetails: {
                paymentMethod: null,
                subtype: null,
                subMethod: null,
            },
            paymentNoticeModal: {
                show: false,
                close: false,
                closeable: false,
                title: '',
                text: '',
            },
            billingData: {
                first_name: null,
                last_name: null,
                street: null,
                house: null,
                postcode: null,
                city: null,
                country: null,
            },
            paymentMethodClickEvent: null,
            previousPaymentMethod: null,
        };
    },
    methods: {
        onPaymentOptionClick($event, paymentMethod, skipBillingDataModal) {
            try {
                if (
                    this.askForBillingData &&
                    this.previousPaymentMethod === paymentMethod.subtype_id
                ) {
                    this.openBillingDetailsModal();
                }

                this.previousPaymentMethod = paymentMethod.subtype_id;
                this.loading = true;
                this.askForBillingData = false;

                $event.preventDefault();

                // Check if we're allowed to skip the billing data modal.
                skipBillingDataModal = typeof skipBillingDataModal === 'boolean' && skipBillingDataModal;

                // Default
                let delayNotice = null;

                // In the case we got a subMethod value, we should inspect the payment method.
                if (typeof paymentMethod.subtype_id !== 'undefined') {
                    this.subMethod = paymentMethod.subtype_id;
                    delayNotice = this.delayNoticeEnabled(paymentMethod) ? paymentMethod.delayNotice : null;
                }

                this.method = paymentMethod.id;

                this.paymentNoticeModal.show = false;
                this.paymentNoticeModal.close = false;
                this.paymentNoticeModal.closeable = false;
                this.paymentNoticeModal.title = '';
                this.paymentNoticeModal.text = '';

                // Default status of modal visibility.
                this.askForBillingData = false;
                this.askForBillingDataDetails.paymentMethod = paymentMethod;
                this.askForBillingDataDetails.subtype = paymentMethod.subtype_id ? paymentMethod : null;
                this.askForBillingDataDetails.subMethod = paymentMethod.subtype_id;

                // If the subtype requires an order we have to check, if the order contains all necessary
                // data like firstname, surname, street and house number to make sure we're able to generate
                // a order at the payment provider, which is needed to handle the payment process.
                if (
                    !skipBillingDataModal &&
                    !!paymentMethod.requiresOrder
                ) {
                    const mandatoryBillingData = {
                        givenName: this.billingData.first_name,
                        familyName: this.billingData.last_name,
                        street: this.billingData.street,
                        houseNumber: this.billingData.house,
                        postalCode: this.billingData.postcode,
                        city: this.billingData.city,
                        country: this.billingData.country,
                    };

                    // If the order is missing a required detail to create the order we have to show
                    // the modal to enter the billing address details. Otherwise, we will restrict the
                    // usage of this method.
                    for (const property in mandatoryBillingData) {
                        // In the case the property contains a valid value, we don't need to ask the
                        // customer for his billing details.
                        if (
                            typeof mandatoryBillingData[property] === 'string' &&
                            mandatoryBillingData[property].trim().length > 0
                        ) {
                            continue;
                        }

                        // Otherwise, if one of these fields is missing we have to ask for the billing
                        // details. In this case we can end the execution of this loop directly.
                        this.askForBillingData = true;
                        break;
                    }
                }

                if (this.askForBillingData) {
                    this.paymentMethodClickEvent = $event;

                    return;
                }

                const wasHandled = this.tryHandlePayment(paymentMethod);
                if (wasHandled) {
                    return;
                }

                // In the case the payment method force a delay notice to be visible, we should
                // show them to the user and wait for a confirmation to continue. Otherwise,
                // we will reset the payment form and give the user another option which he can chose.
                if (typeof delayNotice === 'object' && delayNotice !== null) {
                    this.paymentNoticeModal.title = this.$t(delayNotice.title);
                    this.paymentNoticeModal.text = this.$t(delayNotice.text);
                    this.paymentNoticeModal.show = true;

                    return;
                }

                this.$nextTick(() => {
                    this.$refs.paymentForm.submit();
                });
            } finally {
                this.loading = false;
            }
        },

        onBillingDataFilled() {
            this.fillBillingDetails(true);

            const paymentMethod = this.paymentMethods.find((method) => {
                return method.name.includes('klarna-pay-now');
            });

            this.onPaymentOptionClick(this.paymentMethodClickEvent, paymentMethod, false);
        },

        fillBillingDetails(fromModal = false) {
            let subject = this.order;

            if (fromModal) {
                subject = this.$refs.billingDetailsModal;

                this.billingData.first_name = subject.first_name;
                this.billingData.last_name = subject.last_name;
            } else {
                this.billingData.first_name = subject.first_name || subject.paypal_first_name;
                this.billingData.last_name = subject.last_name || subject.paypal_last_name;
            }

            this.billingData.street = subject.street;
            this.billingData.house = subject.house;
            this.billingData.postcode = subject.postcode;
            this.billingData.city = subject.city;
            this.billingData.country = subject.country;
        },

        showMore() {
            this.showAll = !this.showAll;
        },

        generateFormActionLink(method) {
            return '/' + this.$language + '/backend/payment/start/' + method + '/' + this.order.type + '/' + this.order.id;
        },

        /**
         * Determine if the subtype has a active delay notice.
         *
         * @param {Object} paymentMethod
         *
         * @returns {boolean}
         * */
        delayNoticeEnabled(paymentMethod) {
            // We don't have delay notice definition.
            if (typeof paymentMethod.delayNotice !== 'object' || paymentMethod.delayNotice === null) {
                return false;
            }

            return !(typeof paymentMethod.delayNotice.disabled === 'boolean' && paymentMethod.delayNotice.disabled);
        },

        tryHandlePayment(paymentMethod) {
            switch (paymentMethod.id) {
                case 'gift_voucher':
                    this.$refs.giftVoucherPaymentModal.onShowModal();
                    return true;
            }

            return false;
        },

        openBillingDetailsModal() {
            this.$refs.billingDetailsModal.showModal();
        },
    },
    computed: {
        giftVoucherMethodAvailable() {
            return this.paymentMethods.some(method => method.id === 'gift_voucher');
        },

        /**
         * Define the url of form tag and async request to initialize the payment process.
         *
         * @returns {string}
         */
        formAction() {
            return this.generateFormActionLink(this.method);
        },

        filteredPaymentMethods() {
            return this.showAll ? this.paymentMethods : this.paymentMethods.slice(0, 3);
        },

        /**
         * Handle the search for enabled delay notice to show.
         *
         * @returns {boolean}
         * */
        hasEnabledDelayNotice() {
            return this.paymentMethods.some(paymentMethod => this.delayNoticeEnabled(paymentMethod));
        },
    },
    watch: {
        askForBillingData(val) {
            if (val) {
                this.openBillingDetailsModal();
            }
        },
    },

    mounted() {
        this.fillBillingDetails();
    },
};
</script>

<style lang="scss" scoped>
@keyframes placeholder-glow {
    50% {
        opacity: .2;
    }
}

.placeholder-glow .placeholder {
    animation: placeholder-glow 2s ease-in-out infinite;
}

.placeholder {
    width: 100%;
    display: inline-block;
    min-height: 1em;
    vertical-align: middle;
    cursor: wait;
    background-color: grey;
    opacity: .5;
    animation: placeholder-flow;
    height: 50px;
    border-radius: 5px;
    border: none;
}


fieldset {
    border-width: 2pt;
    border-style: solid;
    border-color: #DEE2E1;
    border-image: initial;
    border-radius: 12px;

    transition: all 0.6s cubic-bezier(1, 0.01, 1, 0.01);

    legend {
        all: revert;
        text-align: center;
        font-size: xx-large;
        font-weight: 600;
        color: var(--main-color);
    }
}

.paymentmethods {
    display: flex;
    flex-direction: column;
    padding: 1rem;

    .paymentmethod {
        flex-grow: 1;

        &:hover {
            cursor: pointer;
        }
    }
}

.more {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    text-decoration: underline !important;
}

.mdi-chevron-right {
    transition: transform 0.3s ease;

    &.active {
        transform: rotate(-90deg);
    }
}

.list-item {
    display: inline-block;
    margin-right: 10px;
}
.list-enter-active, .list-leave-active {
    transition: all 1s;
}
.list-enter, .list-leave-to /* .list-leave-active below version 2.1.8 */ {
    opacity: 0;
    transform: translateX(-30px);
}
</style>
